import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import styled from 'styled-components';
import { Container } from './styled/Container';
import { useLocale } from '../context/LocaleContext';
import { useT } from '../hooks/useT';

const FooterEl = styled.footer`
  background: ${({ theme }) => theme.colors.primary};
  margin-top: 5rem;
`;

const Inner = styled(Container)`
  padding-top: 4rem;
  padding-bottom: 4rem;
`;

const NewsletterBox = styled.div`
  background: ${({ theme }) => theme.colors.maroon};
  border-radius: ${({ theme }) => theme.radiusSm};
  padding: 2rem;
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    padding: 2.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const NewsletterTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.maroonForeground};
  margin: 0;
`;

const NewsletterDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.7);
  margin: 0.25rem 0 0 0;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  @media (min-width: 768px) {
    width: auto;
  }
`;

const EmailWrap = styled.div`
  position: relative;
  flex: 1;
  @media (min-width: 768px) {
    width: 16rem;
  }
`;

const EmailIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
`;

const EmailInput = styled.input`
  width: 100%;
  height: 2.75rem;
  padding-left: 2.5rem;
  padding-right: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primaryForeground};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.875rem;
  outline: none;
`;

const SubscribeBtn = styled.button`
  height: 2.75rem;
  padding: 0 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  display: flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const BrandBox = styled.div``;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
`;

const LogoBox = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  display: flex;
  align-items: center;
  justify-content: center;
  & span {
    color: ${({ theme }) => theme.colors.goldForeground};
    font-weight: 800;
    font-size: 1rem;
  }
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primaryForeground};
`;

const BrandDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0;
`;

const ColumnTitle = styled.h4`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 1rem 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const FooterLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.primaryForeground};
  }
`;

const Bottom = styled.div`
  margin-top: 3rem;
  padding-top: 1.5rem;
  box-shadow: 0 -1px 0 0 rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Copyright = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const BottomLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const BottomLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.2s;
  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
`;

function Footer() {
  const [email, setEmail] = useState('');
  const { prependPath } = useLocale();
  const t = useT();

  return (
    <FooterEl>
      <Inner>
        <NewsletterBox>
          <div>
            <NewsletterTitle>{t('footer.newsletterTitle')}</NewsletterTitle>
            <NewsletterDesc>{t('footer.newsletterDesc')}</NewsletterDesc>
          </div>
          <NewsletterForm onSubmit={(e) => e.preventDefault()}>
            <EmailWrap>
              <EmailIcon>
                <Mail size={16} />
              </EmailIcon>
              <EmailInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
              />
            </EmailWrap>
            <SubscribeBtn type="button">
              {t('footer.subscribe')} <ArrowRight size={14} />
            </SubscribeBtn>
          </NewsletterForm>
        </NewsletterBox>

        <Grid>
          <BrandBox>
            <LogoRow>
              <LogoBox><span>C</span></LogoBox>
              <LogoText>CiteMatch</LogoText>
            </LogoRow>
            <BrandDesc>
              {t('footer.brandDesc')}
            </BrandDesc>
          </BrandBox>
          <div>
            <ColumnTitle>{t('footer.explore')}</ColumnTitle>
            <LinkList>
              <FooterLink to={prependPath('/categories')}>{t('nav.categories')}</FooterLink>
              <FooterLink to={prependPath('/top-sites')}>{t('nav.topSites')}</FooterLink>
              <FooterLink to={prependPath('/compare')}>{t('footer.compareSites')}</FooterLink>
              <FooterLink to={prependPath('/submit')}>{t('nav.submitSite')}</FooterLink>
            </LinkList>
          </div>
          <div>
            <ColumnTitle>{t('footer.popular')}</ColumnTitle>
            <LinkList>
              <FooterLink to={prependPath('/similar/github-com')}>{t('footer.sitesLike')} GitHub</FooterLink>
              <FooterLink to={prependPath('/similar/stripe-com')}>{t('footer.sitesLike')} Stripe</FooterLink>
              <FooterLink to={prependPath('/similar/shopify-com')}>{t('footer.sitesLike')} Shopify</FooterLink>
              <FooterLink to={prependPath('/similar/figma-com')}>{t('footer.sitesLike')} Figma</FooterLink>
            </LinkList>
          </div>
          <div>
            <ColumnTitle>{t('footer.company')}</ColumnTitle>
            <LinkList>
              <FooterLink to={prependPath('/about')}>{t('nav.about')}</FooterLink>
              <FooterLink to={prependPath('/contact')}>{t('footer.contact')}</FooterLink>
              <FooterLink to={prependPath('/remove')}>{t('footer.removeYourSite')}</FooterLink>
              <FooterLink to={prependPath('/privacy')}>{t('footer.privacyPolicy')}</FooterLink>
              <FooterLink to={prependPath('/terms')}>{t('footer.termsOfService')}</FooterLink>
              <FooterLink to={prependPath('/faq')}>{t('footer.faq')}</FooterLink>
            </LinkList>
          </div>
        </Grid>

        <Bottom>
          <Copyright>© 2026 CiteMatch. {t('footer.rights')}</Copyright>
          <BottomLinks>
            <BottomLink to={prependPath('/privacy')}>{t('footer.privacyPolicy')}</BottomLink>
            <BottomLink to={prependPath('/terms')}>{t('footer.termsOfService')}</BottomLink>
            <BottomLink to={prependPath('/contact')}>{t('footer.contact')}</BottomLink>
          </BottomLinks>
        </Bottom>
      </Inner>
    </FooterEl>
  );
}

export default Footer;
